import { useEffect, useRef, useState } from 'react';

// Distance left just before the end (half a cent), so the final frame only ticks the last cent.
const SETTLE_REMAINDER = 0.005;

/**
 * Shrinks the remaining distance by the same factor per unit of time, so each digit settles in turn:
 * the leading digits race to their final value and the cents crawl in at the end.
 */
export const settleDigitByDigit = (end: number, progress: number) => {
  if (progress >= 1) return end;
  if (end <= SETTLE_REMAINDER) return end * progress;

  return end - end * (SETTLE_REMAINDER / end) ** progress;
};

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

type AnimatedNumberOptions = {
  durationMs: number;
  delayMs?: number;
  /** Must be stable (e.g. memoised); a new function restarts the animation. */
  format: (value: number) => string;
};

/**
 * Counts up to `end` by writing each frame straight into the element behind `ref`, so React renders only
 * when the count starts and when it settles. Render `format(value)` as the element's text.
 */
export const useAnimatedNumber = <T extends HTMLElement = HTMLElement>(
  end: number,
  { durationMs, delayMs = 0, format }: AnimatedNumberOptions
) => {
  const ref = useRef<T>(null);
  const shouldAnimate = durationMs > 0 && !prefersReducedMotion();
  const [settledEnd, setSettledEnd] = useState<number | null>(null);
  const isSettled = !shouldAnimate || settledEnd === end;

  useEffect(() => {
    if (!shouldAnimate) return;

    let frame = 0;
    let startedAt: number | undefined;

    const tick = (now: number) => {
      startedAt ??= now + delayMs;
      const progress = Math.min(Math.max((now - startedAt) / durationMs, 0), 1);

      if (progress >= 1) {
        setSettledEnd(end);
        return;
      }

      if (ref.current) ref.current.textContent = format(settleDigitByDigit(end, progress));
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [end, durationMs, delayMs, format, shouldAnimate]);

  return { ref, value: isSettled ? end : 0, isSettled };
};
